import { StrictMode, useState, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { MsalProvider } from '@azure/msal-react'
import { ThemeProvider, createTheme, type ITheme } from '@fluentui/react'
import { registerLicense } from '@syncfusion/ej2-base'
import './index.css'
import App from './App.tsx'
import { msalInstance, initializeMsal, AuthProvider } from './auth'

const useStrictMode = !import.meta.env.DEV;

function renderRoot(element: React.ReactNode) {
  if (useStrictMode) {
    return <StrictMode>{element}</StrictMode>;
  }

  return <>{element}</>;
}

// Light theme
const lightTheme: ITheme = createTheme({
  palette: {
    themePrimary: '#0078d4',
    themeLighterAlt: '#eff6fc',
    themeLighter: '#deecf9',
    themeLight: '#c7e0f4',
    themeTertiary: '#71afe5',
    themeSecondary: '#2b88d8',
    themeDarkAlt: '#106ebe',
    themeDark: '#005a9e',
    themeDarker: '#004578',
    neutralLighterAlt: '#faf9f8',
    neutralLighter: '#f3f2f1',
    neutralLight: '#edebe9',
    neutralQuaternaryAlt: '#e1dfdd',
    neutralQuaternary: '#d0d0d0',
    neutralTertiaryAlt: '#c8c6c4',
    neutralTertiary: '#a19f9d',
    neutralSecondary: '#605e5c',
    neutralPrimaryAlt: '#3b3a39',
    neutralPrimary: '#323130',
    neutralDark: '#201f1e',
    black: '#000000',
    white: '#ffffff',
  },
  semanticColors: {
    bodyBackground: '#ffffff',
    bodyText: '#323130',
    messageText: '#323130',
    errorBackground: '#fde7e9',
    errorText: '#a80000',
    warningBackground: '#fff4ce',
    warningText: '#333333',
    infoBackground: '#f3f2f1',
    successBackground: '#dff6dd',
    successText: '#107c10',
  },
});

Object.assign(lightTheme.semanticColors, {
  cautionBackground: '#fff9db',
  cautionText: '#8a6d00',
});

// Dark theme
const darkTheme: ITheme = createTheme({
  palette: {
    themePrimary: '#3aa0f3',
    themeLighterAlt: '#02060a',
    themeLighter: '#091823',
    themeLight: '#112d43',
    themeTertiary: '#235a85',
    themeSecondary: '#3385c3',
    themeDarkAlt: '#4ba8f4',
    themeDark: '#65b4f6',
    themeDarker: '#8ac7f8',
    neutralLighterAlt: '#1c1c1c',
    neutralLighter: '#252525',
    neutralLight: '#343434',
    neutralQuaternaryAlt: '#3d3d3d',
    neutralQuaternary: '#454545',
    neutralTertiaryAlt: '#6d6d6d',
    neutralTertiary: '#c8c8c8',
    neutralSecondary: '#d0d0d0',
    neutralPrimaryAlt: '#dadada',
    neutralPrimary: '#ffffff',
    neutralDark: '#f4f4f4',
    black: '#f8f8f8',
    white: '#0f0f0f',
  },
  semanticColors: {
    bodyBackground: '#1c1c1c',
    bodyText: '#ffffff',
    messageText: '#ffffff',
    errorBackground: '#442726',
    errorText: '#f1707b',
    warningBackground: '#433519',
    warningText: '#ffaa44',
    infoBackground: '#2b2b2b',
    successBackground: '#27402b',
    successText: '#6ccb5f',
  },
});

Object.assign(darkTheme.semanticColors, {
  cautionBackground: '#3f3a12',
  cautionText: '#ffe082',
});

// Wrapper component to handle theme switching
function ThemedApp() {
  const [isDark, setIsDark] = useState(() => 
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => setIsDark(e.matches);
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <MsalProvider instance={msalInstance}>
        <AuthProvider>
          <App />
        </AuthProvider>
      </MsalProvider>
    </ThemeProvider>
  );
}

// Register Syncfusion license
const syncfusionLicense = import.meta.env.VITE_SYNCFUSION_LICENSE_KEY;
if (syncfusionLicense) {
  registerLicense(syncfusionLicense);
} else {
  console.warn('Syncfusion license key not found. Please set VITE_SYNCFUSION_LICENSE_KEY in your .env file.');
}

// Initialize MSAL before rendering the app
initializeMsal()
  .then(() => {
    console.log('MSAL initialized successfully');
    
    // Render the app
    createRoot(document.getElementById('root')!).render(
      renderRoot(<ThemedApp />),
    );
  })
  .catch((error) => {
    console.error('Failed to initialize MSAL:', error);
    
    // Render error message
    createRoot(document.getElementById('root')!).render(
      renderRoot(
        <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#1c1c1c', color: '#ffffff', minHeight: '100vh' }}>
          <h1>Authentication Error</h1>
          <p>Failed to initialize authentication. Please refresh the page.</p>
          <p style={{ color: '#ff6b6b' }}>{error.message}</p>
        </div>,
      ),
    );
  });
